import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface BetaCredits {
  draftCredits: number;
  matchupCredits: number;
  /** Combined balance across both pools */
  totalCredits: number;
  isBetaUser: boolean;
  loading: boolean;
  /** Deduct from combined pool (draft first, then matchup) */
  deductCredits: (amount: number) => Promise<boolean>;
  /** @deprecated Use deductCredits instead */
  deductDraftCredits: (amount: number) => Promise<boolean>;
  /** @deprecated Use deductCredits instead */
  deductMatchupCredits: (amount: number) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useBetaCredits(): BetaCredits {
  const { user } = useAuth();
  const [draftCredits, setDraftCredits] = useState(0);
  const [matchupCredits, setMatchupCredits] = useState(0);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const { data } = await supabase
      .from('profiles')
      .select('draft_sabotage_credits, matchup_sabotage_credits, beta_credits_granted')
      .eq('id', user.id)
      .single();
    if (data) {
      setDraftCredits((data as any).draft_sabotage_credits ?? 0);
      setMatchupCredits((data as any).matchup_sabotage_credits ?? 0);
      setIsBetaUser((data as any).beta_credits_granted ?? false);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const totalCredits = draftCredits + matchupCredits;

  /** Deduct from combined pool: draft credits first, overflow to matchup */
  const deductCredits = useCallback(async (amount: number): Promise<boolean> => {
    if (!user || totalCredits < amount) return false;

    let remaining = amount;
    const fromDraft = Math.min(remaining, draftCredits);
    remaining -= fromDraft;
    const fromMatchup = Math.min(remaining, matchupCredits);

    const newDraft = draftCredits - fromDraft;
    const newMatchup = matchupCredits - fromMatchup;

    const { error } = await supabase
      .from('profiles')
      .update({
        draft_sabotage_credits: newDraft,
        matchup_sabotage_credits: newMatchup,
      } as any)
      .eq('id', user.id);
    if (error) return false;

    setDraftCredits(newDraft);
    setMatchupCredits(newMatchup);
    return true;
  }, [user, draftCredits, matchupCredits, totalCredits]);

  // Legacy wrappers — both now deduct from combined pool
  const deductDraftCredits = deductCredits;
  const deductMatchupCredits = deductCredits;

  return {
    draftCredits,
    matchupCredits,
    totalCredits,
    isBetaUser,
    loading,
    deductCredits,
    deductDraftCredits,
    deductMatchupCredits,
    refresh: fetchCredits,
  };
}
